// Catalog.ID Client — The owner's own voucher codes
// ============================================================================
// D19: **the buyer keeps its codes.** No server stores a voucher code, an
// invitation code, a lock key or a wallet ref beside an identity — the CID node
// holds hashes and the Worker holds public keys, so neither can produce a code
// or find the money it opens. An organization that buys delegate vouchers
// therefore has exactly two copies of each code: the paper it prints, and this
// list.
//
// It lives in an encrypted device slot (`device-slots.ts`) under the OWNER's
// device key, which has two consequences the pane's copy must state plainly:
//
//   * *Forget this device* deletes it. That is correct — a de-enrolled browser
//     must not keep money — and it is why the pane offers Print in the same
//     breath as it shows a code.
//   * A second device of the same owner does not see it. The codes are not
//     synced anywhere, because syncing them would mean a server holding them.
//
// A lost code is lost bits until its lock expires (D26). Nothing here can undo
// that; the point is to make losing one take a deliberate act.

import { normalizeVoucherCode } from '../auth/voucher-code';
import { normaliseInvitationCode } from '../auth/invitation-document';
import { getDeviceSlot, setDeviceSlot, VOUCHER_CODES_SLOT } from './device-slots';
import type { RegistrationType } from '../api/payment';

/** How many codes one device keeps. A checkout mints at most 25 (D24), so this
 *  is twenty batches — well past any organization's working set, and a bound is
 *  what keeps a localStorage value from growing for ever. */
export const MAX_STORED_CODES = 500;

/** One code an owner bought and has not yet handed out. */
export interface StoredVoucherCode {
    /** Canonical `RGV-` form. */
    code: string;
    registrationType: RegistrationType;
    /** What the buyer put on it. Display only — no server knows this number. */
    bits?: number;
    /** The voucher ROW's expiry, as the node reported it at purchase. */
    expiresAt?: string;
    /** Set once this voucher became an invitation (§4.4). BOTH codes are kept:
     *  the invitation code is what the invitee types, and the VOUCHER code is
     *  the only thing that can reclaim the bits if they never do. */
    invitationCode?: string;
    /** ms epoch, for ordering. */
    addedAt: number;
}

/** What a caller hands in — `addedAt` is stamped here. */
export interface NewStoredVoucherCode {
    code: string;
    registrationType: RegistrationType;
    bits?: number;
    expiresAt?: string;
}

function isStored(value: unknown): value is StoredVoucherCode {
    const v = value as StoredVoucherCode;
    return !!v && typeof v.code === 'string' && typeof v.addedAt === 'number';
}

/** Every code this device holds for `cid`, NEWEST FIRST. Empty when the device
 *  key is unknown (locked tab), when the member chose no local storage, or when
 *  nothing was ever stored — a caller cannot tell those apart and does not need
 *  to: in all three the answer is "type or paste a code". */
export async function loadStoredCodes(cid?: string): Promise<StoredVoucherCode[]> {
    const raw = await getDeviceSlot<unknown[]>(VOUCHER_CODES_SLOT, cid);
    if (!Array.isArray(raw)) return [];
    return raw.filter(isStored).sort((a, b) => b.addedAt - a.addedAt);
}

/** The codes still free to hand out — nothing has been issued against them. */
export async function loadUnusedCodes(cid?: string): Promise<StoredVoucherCode[]> {
    return (await loadStoredCodes(cid)).filter(c => !c.invitationCode);
}

/**
 * Add freshly bought codes. Returns whether they were actually stored — false
 * on a device that holds no key or refuses storage, which is NOT a failure of
 * the purchase: the codes are on screen and the pane offers Print.
 *
 * Malformed codes are dropped rather than stored: a value that is not a voucher
 * code cannot open anything, and keeping it would put a broken row in front of
 * an owner for ever.
 */
export async function addStoredCodes(
    cid: string | undefined, codes: readonly NewStoredVoucherCode[],
): Promise<boolean> {
    const existing = await loadStoredCodes(cid);
    const known = new Set(existing.map(c => c.code));
    const now = Date.now();

    const added: StoredVoucherCode[] = [];
    for (const entry of codes) {
        const code = normalizeVoucherCode(entry.code);
        if (!code || known.has(code)) continue;
        known.add(code);
        added.push({
            code,
            registrationType: entry.registrationType,
            ...(entry.bits !== undefined ? { bits: entry.bits } : {}),
            ...(entry.expiresAt !== undefined ? { expiresAt: entry.expiresAt } : {}),
            addedAt: now,
        });
    }
    if (added.length === 0) return existing.length > 0;

    // Newest first, then truncate: the codes an owner just bought are the ones
    // they are about to use, so an overflowing list drops the oldest.
    const merged = [...added, ...existing].slice(0, MAX_STORED_CODES);
    return setDeviceSlot(VOUCHER_CODES_SLOT, merged, cid);
}

/** Record that `code` became `invitationCode`. Both are kept — see the field's
 *  own note. A code this device does not hold is ignored: an owner may perfectly
 *  well paste a code from paper. */
export async function markCodeIssued(
    cid: string | undefined, code: string, invitationCode: string,
): Promise<boolean> {
    const normalised = normalizeVoucherCode(code);
    const invitation = normaliseInvitationCode(invitationCode);
    if (!normalised || !invitation) return false;
    const existing = await loadStoredCodes(cid);
    let touched = false;
    const next = existing.map(entry => {
        if (entry.code !== normalised) return entry;
        touched = true;
        return { ...entry, invitationCode: invitation };
    });
    if (!touched) return false;
    return setDeviceSlot(VOUCHER_CODES_SLOT, next, cid);
}

/** Forget one code. The bits it opens stay on the Worker until the lock expires
 *  (D26) — this only stops the pane offering it. */
export async function removeStoredCode(
    cid: string | undefined, code: string,
): Promise<boolean> {
    const normalised = normalizeVoucherCode(code);
    if (!normalised) return false;
    const next = (await loadStoredCodes(cid)).filter(c => c.code !== normalised);
    return setDeviceSlot(VOUCHER_CODES_SLOT, next, cid);
}
